import { z } from "zod";

/**
 * The cross-field rules for a "Venue TBA" event: the city and date are known,
 * the venue is not. Applied by the `*SubmitSchema` variants only: the plain
 * objects stay ZodObjects because `.shape`/`.merge` read them.
 */
interface VenueTbaFields {
  venueTba?: boolean | null;
  venue?: unknown;
  mapsUrl?: string | null;
  geolocation?: { latitude: number; longitude: number };
  timezone?: string;
}

/**
 * True for a real map point. z.coerce.number() turns null into 0, so the 0,0
 * placeholder is also what a missing city centre looks like: it never counts.
 */
export function hasCityCentre(
  geolocation: { latitude?: unknown; longitude?: unknown } | null | undefined,
): boolean {
  const latitude = geolocation?.latitude;
  const longitude = geolocation?.longitude;
  return (
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  );
}

export function checkVenueTba(
  data: VenueTbaFields,
  ctx: z.RefinementCtx,
): void {
  if (!data.venueTba) return;

  if (data.venue) {
    ctx.addIssue({
      code: "custom",
      message: "A Venue TBA event cannot have a venue",
      path: ["venueTba"],
    });
  }
  if (data.mapsUrl) {
    ctx.addIssue({
      code: "custom",
      message: "A Venue TBA event cannot have a map link",
      path: ["venueTba"],
    });
  }

  const hasCentre = hasCityCentre(data.geolocation);
  if (!hasCentre) {
    ctx.addIssue({
      code: "custom",
      message: "A Venue TBA event needs the city's centre point",
      path: ["geolocation"],
    });
  }

  if (!data.timezone) {
    ctx.addIssue({
      code: "custom",
      message: "A Venue TBA event needs a timezone",
      path: ["timezone"],
    });
  }
}
